export const CONSENT_CATEGORIES = [
  'necessary',
  'functional',
  'analytics',
  'marketing',
  'support',
] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export interface ConsentSnapshot {
  ready: boolean;
  version: number | null;
  updatedAt: number | null;
  categories: Record<ConsentCategory, boolean>;
}

interface PerspectifyConsentState {
  version: number;
  updatedAt: number;
  categories: Partial<Record<ConsentCategory, boolean>>;
}

interface PerspectifyConsentBridge {
  getState(): PerspectifyConsentState | null;
  openPreferences(): void;
  onChange(listener: (state: PerspectifyConsentState | null) => void): () => void;
  canUse(key: ConsentCategory | 'perspectify_recorder' | 'perspectify_feedback_widget'): boolean;
}

interface PerspectifyBrowserApi {
  init(config: {
    apiKey: string;
    assetsBaseUrl: string;
    trigger: { type: 'none' };
    recorder: { enabled: false };
    consent: {
      enabled: true;
      projectId: string;
      manageButtonPosition: 'bottom-left';
      manageButtonVisible: true;
      bannerVisible: true;
      autoConsent: false;
    };
  }): Promise<unknown>;
}

declare global {
  interface Window {
    PerspectifyBrowser?: PerspectifyBrowserApi;
    PerspectifyConsent?: PerspectifyConsentBridge;
  }
}

const PERSPECTIFY_PROJECT_ID = 'd0268c9e-596d-4797-8a0e-af29c51cb845';
// This is Perspectify's browser install key. It is intentionally public and is
// shipped to every visitor in the recommended browser snippet.
const PERSPECTIFY_PUBLIC_INSTALL_KEY = 'mon3_9dfa7264297e7296505f26c4f87da44b93c0bb3';
const PERSPECTIFY_BUNDLE_URL = 'https://app.perspectify.dev/assets/browser/perspectify-browser.js';
const PERSPECTIFY_SCRIPT_ID = 'perspectify-consent-browser';

const deniedSnapshot = (): ConsentSnapshot => ({
  ready: false,
  version: null,
  updatedAt: null,
  categories: {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
    support: false,
  },
});

class ConsentService {
  private snapshot = deniedSnapshot();
  private listeners = new Set<(snapshot: ConsentSnapshot) => void>();
  private initializePromise: Promise<void> | null = null;
  private unsubscribeFromPerspectify: (() => void) | null = null;

  public initialize(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    if (!this.initializePromise) {
      this.initializePromise = this.initializePerspectify().catch((error) => {
        console.error('ConsentService: Perspectify initialization failed; optional services remain disabled.', error);
        this.publish(deniedSnapshot());
      });
    }
    return this.initializePromise;
  }

  public subscribe(listener: (snapshot: ConsentSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  public getSnapshot(): ConsentSnapshot {
    return this.snapshot;
  }

  public canUse(category: ConsentCategory): boolean {
    return category === 'necessary' || this.snapshot.categories[category] === true;
  }

  public openPreferences(): void {
    window.PerspectifyConsent?.openPreferences();
  }

  private async initializePerspectify(): Promise<void> {
    await this.loadBrowserBundle();

    if (!window.PerspectifyBrowser) {
      throw new Error('Perspectify browser API is unavailable after the bundle loaded.');
    }

    await window.PerspectifyBrowser.init({
      apiKey: PERSPECTIFY_PUBLIC_INSTALL_KEY,
      assetsBaseUrl: new URL('.', PERSPECTIFY_BUNDLE_URL).toString(),
      trigger: { type: 'none' },
      recorder: { enabled: false },
      consent: {
        enabled: true,
        projectId: PERSPECTIFY_PROJECT_ID,
        manageButtonPosition: 'bottom-left',
        manageButtonVisible: true,
        bannerVisible: true,
        autoConsent: false,
      },
    });

    const bridge = window.PerspectifyConsent;
    if (!bridge) {
      // A disabled or unavailable policy must never enable optional services.
      this.publish(deniedSnapshot());
      return;
    }

    this.unsubscribeFromPerspectify?.();
    this.unsubscribeFromPerspectify = bridge.onChange((state) => this.syncState(state));
    this.syncState(bridge.getState());
  }

  private loadBrowserBundle(): Promise<void> {
    if (window.PerspectifyBrowser) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const existing = document.getElementById(PERSPECTIFY_SCRIPT_ID) as HTMLScriptElement | null;
      const script = existing ?? document.createElement('script');

      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Timed out while loading the Perspectify browser bundle.'));
      }, 10_000);

      const cleanup = () => {
        window.clearTimeout(timeout);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
      };
      const handleLoad = () => {
        cleanup();
        resolve();
      };
      const handleError = () => {
        cleanup();
        reject(new Error('Failed to load the Perspectify browser bundle.'));
      };

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existing) {
        script.id = PERSPECTIFY_SCRIPT_ID;
        script.src = PERSPECTIFY_BUNDLE_URL;
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }

  private syncState(state: PerspectifyConsentState | null): void {
    if (!state) {
      this.publish(deniedSnapshot());
      return;
    }

    const categories = deniedSnapshot().categories;
    for (const category of CONSENT_CATEGORIES) {
      categories[category] = category === 'necessary' || state.categories[category] === true;
    }

    this.publish({
      ready: true,
      version: state.version,
      updatedAt: state.updatedAt,
      categories,
    });
  }

  private publish(snapshot: ConsentSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener(snapshot);
  }
}

export const consentService = new ConsentService();
